/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import { EventBusModule } from "@vlinder-web/event-bus-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { BaseApplication } from "./base/baseApplication";
import { UnitTest } from "./unit.test";
import { StorageModule } from "@vlinder-web/storage-module-react";
import {
  AppNavigatorService,
  BreadcrumbService,
  LandingPageService,
  PrivacyPageService,
  TermsPageService,
  ProfilePageService,
  DashboardOverviewService,
  SignInPageService,
  SignUpPageService,
  ResetPasswordPageService,
  ForgotPasswordPageService,
  RegisterPageService,
  ProductLandingPageService,
  ProductPageService,
  ProductDetailPageService,
  BadgeDetailPageService,
  GetOCRPageService,
} from "../services";
import { BOOTSTRAP_CONFIG } from "../config/bootstrap.config"

import { DEFAULT_APP_CONFIG as DEF_CONFIG } from "../config/default.config";

export class VApplication extends BaseApplication {
  private static instance: VApplication;

  private constructor(options: any = {}) {
    super(options);

    this.setupBindings();

    //Modules
    this.initModules();

    //Services
    this.initServices();
  }

  static getInstance(options: any = {}) {
    if (!this.instance) {
      this.instance = new VApplication(options);
    }
    return this.instance;
  }

  // @ts-ignore
  async start() {
    await this.initRunLevel();
    await this.unitTest();
  }

  setupBindings() {
    //------------->>>>>--------------//
    const storageService = StorageModule.getInstance();
    const DEFAULT_APP_CONFIG = storageService.get("appConfig", "session");

    this.configure("services.RegisterPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      signerPK: DEF_CONFIG?.SIGNER_PK,
    });

    this.configure("services.ProductPageService", {
      restAPIURL: DEFAULT_APP_CONFIG?.servers?.rest?.restServer?.baseURL
    });

    this.configure("services.GetOCRPageService", {
      getOCRRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.restServer?.ocrBaseURL,
    });

    this.configure("services.SignInPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });
    //------------->>>>>--------------//
    this.configure("services.SignUpPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      termsURL: DEFAULT_APP_CONFIG?.organization?.termsURL,
    });
    //------------->>>>>--------------//
    this.configure("services.ForgotPasswordPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
    });
    //------------->>>>>--------------//
    this.configure("services.ResetPasswordPageService", {
      loginRestAPI: DEFAULT_APP_CONFIG?.servers?.rest?.loginServer?.baseURL,
      headers: DEFAULT_APP_CONFIG?.headers,
      signerPK: DEF_CONFIG?.SIGNER_PK,
    });
    //------------->>>>>--------------//
  }

  async initRunLevel() {
    const storageSrv = StorageModule.getInstance();
    const DEFAULT_APP_CONFIG = storageSrv.get("appConfig", "session");
    //------------->>>>>--------------//
    const graphqlModule = this.getSync(
      "services.GraphqlServerModule"
    ) as GraphqlServerModule;
    if (graphqlModule) {
      if (DEFAULT_APP_CONFIG.servers.graphql) {
        type TClient = (typeof DEFAULT_APP_CONFIG.servers.graphql)[0];
        DEFAULT_APP_CONFIG?.servers.graphql.forEach((client: TClient) => {
          graphqlModule
            .createClient(client.name, {
              url: client.url,
              getContext: this.getGraphQLContext.bind(this),
            })
            .then((res) => {
              console.log(`initRunLevel check`, res);
            })
            .catch((err) => {
              console.log(`error in initRunLevel  ${err.message}`);
            });
        });
      } else {
        type TClient = (typeof BOOTSTRAP_CONFIG.servers.graphql)[0];
        BOOTSTRAP_CONFIG?.servers.graphql.forEach((client: TClient) => {
          graphqlModule
            .createClient(client.name, {
              url: client.url,
              getContext: this.getGraphQLContext.bind(this),
            })
            .then((res) => {
              console.log(`initRunLevel check`, res);
            })
            .catch((err) => {
              console.log(`error in initRunLevel  ${err.message}`);
            });
        });
      }
    }
    //------------->>>>>--------------//
    const storageService = this.getSync(
      "services.StorageModule"
    ) as StorageModule;
    await storageService.init();
  }

  async unitTest() {
    const ut = new UnitTest(VApplication);
    ut.run();
  }

  initModules() {
    this.service("services.EventBusModule", EventBusModule);
    this.service("services.GraphqlServerModule", GraphqlServerModule);
  }

  initServices() {
    this.service("services.AppNavigatorService", AppNavigatorService);
    this.service("services.BreadcrumbService", BreadcrumbService);
    this.service("services.LandingPageService", LandingPageService);
    this.service("services.PrivacyPageService", PrivacyPageService);
    this.service("services.TermsPageService", TermsPageService);
    this.service("services.ProfilePageService", ProfilePageService);
    this.service("services.DashboardOverviewService", DashboardOverviewService);
    this.service("services.SignInPageService", SignInPageService);
    this.service("services.SignUpPageService", SignUpPageService);
    this.service(
      "services.ForgotPasswordPageService",
      ForgotPasswordPageService
    );
    this.service("services.ResetPasswordPageService", ResetPasswordPageService);
    this.service("services.RegisterPageService", RegisterPageService);
    this.service(
      "services.ProductLandingPageService",
      ProductLandingPageService
    );
    this.service("services.ProductPageService", ProductPageService);
    this.service("services.ProductDetailPageService", ProductDetailPageService);
    this.service("services.BadgeDetailPageService", BadgeDetailPageService);
    this.service("services.GetOCRPageService", GetOCRPageService);
  }

  private getGraphQLContext() {
    const loginService = this.getSync(
      "services.AppNavigatorService"
    ) as AppNavigatorService;
    let token = loginService?.getToken();
    //FIXME:
    if (token) {
      return {
        headers: {
          authentication: token,
          headers: {
            // ...{ ...deviceInfoService?.getDeviceInfo() },
            // ...{ ...deviceInfoService?.getAppInfo() },
          },
        },
      };
    } else {
      return {
        headers: {
          headers: {
            // ...{ ...deviceInfoService?.getDeviceInfo() },
            // ...{ ...deviceInfoService?.getAppInfo() },
          },
        },
      };
    }
  }
}
